// @ts-nocheck
/**
 * Create/Edit PBX — dual-mode (create + edit via useParams id).
 * Edit mode disables name/ip/port/gateway fields; trunks stay editable (min one). IPv4 regex on blur,
 * port digit-only 5-digit cap.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  API_MANAGE_GROUP_LISTING,
  API_MANAGE_LOCATIONS_LIST,
  API_TRUNKS_LIST,
  API_MANAGE_DIALER_CREATE,
  API_MANAGE_DIALER_DETAIL,
  API_MANAGE_DIALER_UPDATE,
  API_SERVER_MANAGEMENT_LIST,
} from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall } from '@/service/getApiCall';
import { postApiCall } from '@/service/postApiCall';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import Loader from '@/components/partials/components/Loader';
import { ArrowLeft, Plus, Save, X, Users, Network, MapPin, Server } from 'lucide-react';

const _label = 'text-sm font-medium text-foreground';
const _star = <span className="text-destructive ml-0.5">*</span>;

const CreateDialer = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();

  const [groups, setGroups] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [addedGroups, setAddedGroups] = useState<any[]>([]);

  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState('');

  const [trunks, setTrunks] = useState<any[]>([]);
  const [selectedTrunk, setSelectedTrunk] = useState('');
  const [addedTrunks, setAddedTrunks] = useState<any[]>([]);

  const [servers, setServers] = useState<any[]>([]);
  const [selectedServer, setSelectedServer] = useState('');

  const [dialerName, setDialerName] = useState('');
  const [ip, setIp] = useState('');
  const [port, setPort] = useState('');

  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [groupsData, locationsData, trunksData, serversData]: any = await Promise.all([
          getApiCall(API_MANAGE_GROUP_LISTING, {}),
          getApiCall(API_MANAGE_LOCATIONS_LIST, {}),
          getApiCall(API_TRUNKS_LIST, {}),
          getApiCall(API_SERVER_MANAGEMENT_LIST, {}),
        ]);
        setGroups(groupsData || []);
        setLocations(locationsData || []);
        setTrunks(trunksData || []);
        setServers(serversData || []);
      } catch (err) {
        toast.error(friendlyApiError(err));
      }
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getApiCall(API_MANAGE_DIALER_DETAIL(id), {})
      .then((res: any) => {
        if (res) {
          setDialerName(res.dialer_name || '');
          setIp(res.ip || '');
          setPort(res.port?.toString() || '');
          setSelectedLocation(res.location_id?.toString() || '');
          setAddedTrunks(res.trunkMappings?.map((tm: any) => tm.trunk).filter(Boolean) || []);
          setAddedGroups(res.groupMappings?.map((gm: any) => gm.group) || []);
          setSelectedServer(res.server_id?.toString() || '');
        }
      })
      .catch((err) => toast.error(friendlyApiError(err)))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAddGroup = () => {
    if (!selectedGroup) return;
    const exists = addedGroups.find((g) => g.group_id === parseInt(selectedGroup));
    if (exists) return;
    const groupToAdd = groups.find((g) => g.group_id == selectedGroup);
    if (groupToAdd) {
      setAddedGroups([...addedGroups, groupToAdd]);
      setSelectedGroup('');
      if (errors.groups) setErrors((p: any) => ({ ...p, groups: null }));
    }
  };
  const handleRemoveGroup = (gid: any) =>
    setAddedGroups(addedGroups.filter((g) => g.group_id !== gid));

  const handleAddTrunk = () => {
    if (!selectedTrunk) return;
    const exists = addedTrunks.find((t) => t.id == selectedTrunk);
    if (exists) return;
    const trunkToAdd = trunks.find((t) => t.id == selectedTrunk);
    if (trunkToAdd) {
      setAddedTrunks([...addedTrunks, trunkToAdd]);
      setSelectedTrunk('');
      if (errors.trunk) setErrors((p: any) => ({ ...p, trunk: null }));
    }
  };
  const handleRemoveTrunk = (tid: any) =>
    setAddedTrunks(addedTrunks.filter((t) => t.id !== tid));

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const newErrors: any = {};
    if (!dialerName) newErrors.dialerName = 'PBX name is required';
    if (!ip) newErrors.ip = 'IP is required';
    if (!port) newErrors.port = 'Port is required';
    if (!selectedLocation) newErrors.location = 'Dialer location is required';
    if (addedTrunks.length === 0) newErrors.trunk = 'Select at least one trunk';
    if (addedGroups.length === 0) newErrors.groups = 'Select at least one group';
    if (!selectedServer) newErrors.server = 'Gateway is required';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const payload = {
      dialer_name: dialerName,
      ip,
      port: parseInt(port, 10),
      location_id: parseInt(selectedLocation, 10),
      trunk_ids: addedTrunks.map((t) => Number(t.id)),
      groups: addedGroups.map((g) => Number(g.group_id)),
      server_id: selectedServer,
    };

    setSaving(true);
    try {
      if (id) {
        await postApiCall(API_MANAGE_DIALER_UPDATE(id), payload);
        toast.success('PBX updated successfully');
      } else {
        await postApiCall(API_MANAGE_DIALER_CREATE, payload);
        toast.success('PBX created successfully');
      }
      navigate('/dialer-list');
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ScrollPage>
        <Loader />
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title={id ? 'Edit PBX' : 'Create PBX'}
        description="Configure a dialer / PBX instance and its call-centre assignments."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to={'/' + ROUTES.MANAGE_DIALERS.DIALER_LISTING}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <div className="mt-4 rounded-lg border border-border/60 bg-card p-4 shadow-neu-raised">
        <div className="mb-4 flex items-center justify-between gap-3 border-b border-border/60 pb-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Server className="h-4 w-4" />
            </span>
            <div>
              <div className="text-sm font-semibold text-foreground">
                {isEditMode ? 'Update PBX Configuration' : 'New PBX Configuration'}
              </div>
              <div className="text-xs text-muted-foreground">
                Fields marked with <span className="text-destructive">*</span> are required
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic info */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="space-y-1.5">
              <label className={_label}>PBX Name {_star}</label>
              <Input
                type="text"
                placeholder="e.g. pbx-mum-01"
                value={dialerName}
                disabled={isEditMode}
                readOnly={isEditMode}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^[a-zA-Z0-9-]*$/.test(val)) {
                    setDialerName(val);
                    if (errors.dialerName)
                      setErrors((p: any) => ({ ...p, dialerName: null }));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === ' ') e.preventDefault();
                }}
                className={errors.dialerName ? 'border-destructive' : ''}
              />
              {errors.dialerName && (
                <div className="text-xs text-destructive">{errors.dialerName}</div>
              )}
            </div>

            <div className="space-y-1.5">
              <label className={_label}>IP {_star}</label>
              <Input
                type="text"
                placeholder="e.g. 10.0.0.12"
                value={ip}
                disabled={isEditMode}
                readOnly={isEditMode}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^[0-9.]*$/.test(val)) {
                    setIp(val);
                    if (errors.ip) setErrors((p: any) => ({ ...p, ip: null }));
                  }
                }}
                onBlur={() => {
                  const re =
                    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
                  if (ip && !re.test(ip)) {
                    setErrors((p: any) => ({ ...p, ip: 'Invalid IPv4 address' }));
                  }
                }}
                className={errors.ip ? 'border-destructive' : ''}
              />
              {errors.ip && <div className="text-xs text-destructive">{errors.ip}</div>}
            </div>

            <div className="space-y-1.5">
              <label className={_label}>Port {_star}</label>
              <Input
                type="text"
                placeholder="e.g. 5060"
                value={port}
                disabled={isEditMode}
                readOnly={isEditMode}
                maxLength={5}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 5) {
                    setPort(val);
                    if (errors.port) setErrors((p: any) => ({ ...p, port: null }));
                  }
                }}
                onBlur={() => {
                  if (!port || port.length > 5) {
                    setErrors((p: any) => ({
                      ...p,
                      port: 'Port must be numeric and max 5 digits',
                    }));
                  }
                }}
                className={errors.port ? 'border-destructive' : ''}
              />
              {errors.port && (
                <div className="text-xs text-destructive">{errors.port}</div>
              )}
            </div>
          </div>

          {/* Groups picker */}
          <FieldMultiPicker
            icon={<Users className="h-4 w-4" />}
            label="Dialer Groups"
            required
            placeholder="Select a group…"
            options={groups
              .filter((g) => !addedGroups.some((ag) => ag.group_id == g.group_id))
              .map((g) => ({ value: String(g.group_id), label: g.group_name }))}
            value={selectedGroup}
            onChange={setSelectedGroup}
            onAdd={handleAddGroup}
            addedItems={addedGroups.map((g) => ({
              key: g.group_id,
              label: g.group_name,
              onRemove: () => handleRemoveGroup(g.group_id),
            }))}
            emptyMsg="No groups added"
            error={errors.groups}
          />

          {/* Trunks picker */}
          <FieldMultiPicker
            icon={<Network className="h-4 w-4" />}
            label="Trunks"
            required
            placeholder="Select a trunk…"
            options={trunks
              .filter((t) => !addedTrunks.some((at) => at.id == t.id))
              .map((t) => ({ value: String(t.id), label: t.name }))}
            value={selectedTrunk}
            onChange={setSelectedTrunk}
            onAdd={handleAddTrunk}
            addedItems={addedTrunks.map((t) => ({
              key: t.id,
              label: t.name,
              onRemove: () => handleRemoveTrunk(t.id),
              removeDisabled: addedTrunks.length <= 1,
            }))}
            emptyMsg="No trunks added"
            error={errors.trunk}
          />

          {/* Location + Gateway */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className={_label + ' inline-flex items-center gap-1.5'}>
                <MapPin className="h-4 w-4 text-primary" />
                Dialer Location {_star}
              </label>
              <Select
                value={selectedLocation || undefined}
                onValueChange={(v) => {
                  setSelectedLocation(v);
                  if (errors.location) setErrors((p: any) => ({ ...p, location: null }));
                }}
              >
                <SelectTrigger className={'h-10 w-full ' + (errors.location ? 'border-destructive' : '')}>
                  <SelectValue placeholder="Select dialer location…" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.location_id} value={String(loc.location_id)}>
                      {loc.location_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.location && (
                <div className="text-xs text-destructive">{errors.location}</div>
              )}
            </div>

            <div className="space-y-1.5">
              <label className={_label + ' inline-flex items-center gap-1.5'}>
                <Server className="h-4 w-4 text-primary" />
                Gateway {_star}
              </label>
              <Select
                value={selectedServer || undefined}
                onValueChange={(v) => {
                  setSelectedServer(v);
                  if (errors.server) setErrors((p: any) => ({ ...p, server: null }));
                }}
                disabled={isEditMode}
              >
                <SelectTrigger className={'h-10 w-full ' + (errors.server ? 'border-destructive' : '')}>
                  <SelectValue placeholder="Select gateway…" />
                </SelectTrigger>
                <SelectContent>
                  {servers.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.server_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.server && (
                <div className="text-xs text-destructive">{errors.server}</div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-border/60 pt-4">
            <Button asChild variant="ghost" size="sm">
              <Link to={'/' + ROUTES.MANAGE_DIALERS.DIALER_LISTING}>Cancel</Link>
            </Button>
            <Button type="submit" size="sm" disabled={saving} className="gap-1.5">
              {saving ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {id ? 'Update PBX' : 'Create PBX'}
            </Button>
          </div>
        </form>
      </div>
    </ScrollPage>
  );
};

/**
 * Shared "pick from dropdown, click + to add, list below" widget used for
 * Groups and Trunks. Keeps both surfaces visually consistent and cuts down
 * on duplicated JSX in this page.
 */
function FieldMultiPicker({
  icon,
  label,
  required,
  disabled,
  lockedMsg,
  placeholder,
  options,
  value,
  onChange,
  onAdd,
  addedItems,
  emptyMsg,
  error,
}: any) {
  return (
    <div className="space-y-2">
      <label className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
        <span className="text-primary">{icon}</span>
        {label} {required ? <span className="text-destructive">*</span> : null}
      </label>

      {/* Picker row is hidden when the field is locked (e.g. edit mode).
          Read-only chips + a small lock message replace it so the user
          isn't staring at an unusable empty-looking dropdown. */}
      {disabled ? (
        lockedMsg ? (
          <div className="rounded-md border border-dashed border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            {lockedMsg}
          </div>
        ) : null
      ) : (
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Select value={value || undefined} onValueChange={onChange}>
              <SelectTrigger
                className={
                  'h-10 w-full ' + (error && addedItems.length === 0 ? 'border-destructive' : '')
                }
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {options.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    No options available
                  </div>
                ) : (
                  options.map((o: any) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={onAdd}
            disabled={!value}
            className="gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </Button>
        </div>
      )}

      {error && <div className="text-xs text-destructive">{error}</div>}

      {addedItems.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/60 bg-muted/30 py-3 text-center text-xs text-muted-foreground">
          {emptyMsg}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {addedItems.map((it: any) => (
            <span
              key={it.key}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary ring-1 ring-inset ring-primary/25"
            >
              {it.label}
              {!it.removeDisabled && (
                <button
                  type="button"
                  onClick={it.onRemove}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-primary/70 transition-colors hover:bg-rose-500/15 hover:text-rose-600 dark:hover:text-rose-300"
                  title="Remove"
                  aria-label="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default CreateDialer;
